import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Columns3,
  Filter,
  Download,
  LayoutGrid,
  X,
  Check,
  ChevronRight as ChevronExpand,
  ChevronDown as ChevronCollapse,
  Building2,
  FolderOpen,
  Monitor,
} from "lucide-react";
import "./tree-data-table.scss";

// Density options
const DENSITY_OPTIONS = {
  compact: { label: "Compact", padding: "8px 12px" },
  standard: { label: "Standard", padding: "14px 18px" },
  comfortable: { label: "Comfortable", padding: "20px 24px" },
};

// Row type icons
const ROW_ICONS = {
  client: Building2,
  workspace: FolderOpen,
  asset: Monitor,
};

export default function TreeDataTable({
  data = [],
  columns = [],
  enableSearch = true,
  enableFilters = true,
  enableColumnVisibility = true,
  enableSorting = true,
  enablePagination = true,
  enableExport = true,
  enableDensity = true,
  onRowClick,
  pageSize = 10,
  pageSizeOptions = [5, 10, 20, 50, 100],
  emptyMessage = "No data available",
  loading = false,
  className = "",
  defaultExpandedLevel = 0, // 0 = all collapsed, 1 = clients expanded, 2 = workspaces expanded
  initialColumnVisibility = {}, // { columnId: true/false } - columns not specified default to true
}) {
  // State
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() =>
    columns.reduce(
      (acc, col) => ({
        ...acc,
        [col.id]:
          initialColumnVisibility[col.id] !== undefined
            ? initialColumnVisibility[col.id]
            : true,
      }),
      {}
    )
  );
  const [density, setDensity] = useState("standard");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Dropdown states
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [showDensityDropdown, setShowDensityDropdown] = useState(false);

  // Refs for click outside
  const columnsRef = useRef(null);
  const filtersRef = useRef(null);
  const densityRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target)) {
        setShowColumnsDropdown(false);
      }
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFiltersDropdown(false);
      }
      if (densityRef.current && !densityRef.current.contains(event.target)) {
        setShowDensityDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize expanded rows based on defaultExpandedLevel
  useEffect(() => {
    if (defaultExpandedLevel > 0) {
      const initialExpanded = new Set();
      data.forEach((client) => {
        if (defaultExpandedLevel >= 1) {
          initialExpanded.add(`client-${client.clientUUID}`);
        }
        if (defaultExpandedLevel >= 2 && client.workspaces) {
          client.workspaces.forEach((workspace) => {
            initialExpanded.add(`workspace-${workspace.workspaceUUID}`);
          });
        }
      });
      setExpandedRows(initialExpanded);
    }
  }, [data, defaultExpandedLevel]);

  // Flatten tree data for display
  const flattenedData = useMemo(() => {
    const rows = [];

    const filterText = globalFilter.toLowerCase();

    data.forEach((client) => {
      // Check if client matches filter
      const clientMatches =
        !filterText ||
        client.clientName?.toLowerCase().includes(filterText) ||
        client.description?.toLowerCase().includes(filterText);

      // Check if any workspace or asset matches
      let hasMatchingChildren = false;
      if (client.workspaces) {
        client.workspaces.forEach((workspace) => {
          if (
            workspace.workspaceName?.toLowerCase().includes(filterText) ||
            workspace.description?.toLowerCase().includes(filterText)
          ) {
            hasMatchingChildren = true;
          }
          if (workspace.assets) {
            workspace.assets.forEach((asset) => {
              if (
                asset.deviceName?.toLowerCase().includes(filterText) ||
                asset.hostname?.toLowerCase().includes(filterText) ||
                asset.ip?.toLowerCase().includes(filterText)
              ) {
                hasMatchingChildren = true;
              }
            });
          }
        });
      }

      if (clientMatches || hasMatchingChildren || !filterText) {
        // Add client row
        rows.push({
          type: "client",
          level: 0,
          id: `client-${client.clientUUID}`,
          data: client,
          hasChildren: client.workspaces && client.workspaces.length > 0,
          isExpanded: expandedRows.has(`client-${client.clientUUID}`),
        });

        // If client is expanded, add workspaces
        if (
          expandedRows.has(`client-${client.clientUUID}`) &&
          client.workspaces
        ) {
          client.workspaces.forEach((workspace) => {
            const workspaceMatches =
              !filterText ||
              workspace.workspaceName?.toLowerCase().includes(filterText) ||
              workspace.description?.toLowerCase().includes(filterText);

            let hasMatchingAssets = false;
            if (workspace.assets) {
              workspace.assets.forEach((asset) => {
                if (
                  asset.deviceName?.toLowerCase().includes(filterText) ||
                  asset.hostname?.toLowerCase().includes(filterText) ||
                  asset.ip?.toLowerCase().includes(filterText)
                ) {
                  hasMatchingAssets = true;
                }
              });
            }

            if (workspaceMatches || hasMatchingAssets || !filterText) {
              // Add workspace row
              rows.push({
                type: "workspace",
                level: 1,
                id: `workspace-${workspace.workspaceUUID}`,
                data: workspace,
                parentId: `client-${client.clientUUID}`,
                hasChildren: workspace.assets && workspace.assets.length > 0,
                isExpanded: expandedRows.has(
                  `workspace-${workspace.workspaceUUID}`
                ),
              });

              // If workspace is expanded, add assets
              if (
                expandedRows.has(`workspace-${workspace.workspaceUUID}`) &&
                workspace.assets
              ) {
                workspace.assets.forEach((asset) => {
                  const assetMatches =
                    !filterText ||
                    asset.deviceName?.toLowerCase().includes(filterText) ||
                    asset.hostname?.toLowerCase().includes(filterText) ||
                    asset.ip?.toLowerCase().includes(filterText);

                  if (assetMatches || !filterText) {
                    rows.push({
                      type: "asset",
                      level: 2,
                      id: `asset-${asset.assetUUID}`,
                      data: asset,
                      parentId: `workspace-${workspace.workspaceUUID}`,
                      hasChildren: false,
                      isExpanded: false,
                    });
                  }
                });
              }
            }
          });
        }
      }
    });

    return rows;
  }, [data, expandedRows, globalFilter]);

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return flattenedData;

    // We only sort top-level items and keep hierarchy
    return [...flattenedData].sort((a, b) => {
      // Only sort items at the same level
      if (a.level !== b.level) return 0;

      const aValue = getCellValue(a, sortConfig.key);
      const bValue = getCellValue(b, sortConfig.key);

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [flattenedData, sortConfig]);

  // Pagination
  const paginatedData = useMemo(() => {
    if (!enablePagination) return sortedData;
    const start = currentPage * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage, enablePagination]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // Get cell value based on row type and column
  function getCellValue(row, columnId) {
    const col = columns.find((c) => c.id === columnId);
    if (!col) return null;

    if (col.accessorFn) {
      return col.accessorFn(row);
    }

    if (col.accessorKey) {
      return row.data[col.accessorKey];
    }

    return null;
  }

  // Toggle row expansion
  const toggleRow = (rowId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  // Toggle all rows
  const toggleAllRows = (expand) => {
    if (expand) {
      const allRowIds = new Set();
      data.forEach((client) => {
        allRowIds.add(`client-${client.clientUUID}`);
        if (client.workspaces) {
          client.workspaces.forEach((workspace) => {
            allRowIds.add(`workspace-${workspace.workspaceUUID}`);
          });
        }
      });
      setExpandedRows(allRowIds);
    } else {
      setExpandedRows(new Set());
    }
  };

  // Handle sort
  const handleSort = (columnId) => {
    setSortConfig((prev) => {
      if (prev.key !== columnId) {
        return { key: columnId, direction: "asc" };
      }
      if (prev.direction === "asc") {
        return { key: columnId, direction: "desc" };
      }
      return { key: null, direction: null };
    });
  };

  // Export to CSV
  const handleExport = () => {
    const visibleColumns = columns.filter((col) => columnVisibility[col.id]);
    const headers = visibleColumns.map((col) => col.header || col.id);

    const rows = flattenedData.map((row) =>
      visibleColumns.map((col) => {
        const value = getCellValue(row, col.id);
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
      })
    );

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `tree-export-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  // Get sort icon
  const getSortIcon = (columnId) => {
    if (sortConfig.key !== columnId) {
      return <ChevronsUpDown className="tdt-sort-icon" />;
    }
    if (sortConfig.direction === "asc") {
      return <ChevronUp className="tdt-sort-icon tdt-sort-icon--active" />;
    }
    return <ChevronDown className="tdt-sort-icon tdt-sort-icon--active" />;
  };

  // Visible columns
  const visibleColumns = columns.filter((col) => columnVisibility[col.id]);
  const visibleColumnsCount = visibleColumns.length;
  const totalColumnsCount = columns.length;

  // Render cell content
  const renderCell = (row, column, isFirstColumn) => {
    const value = getCellValue(row, column.id);
    const Icon = ROW_ICONS[row.type];

    if (isFirstColumn) {
      return (
        <div
          className="tdt-cell-tree"
          style={{ paddingLeft: `${row.level * 24}px` }}
        >
          {row.hasChildren ? (
            <button
              className="tdt-expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleRow(row.id);
              }}
            >
              {row.isExpanded ? (
                <ChevronCollapse size={16} />
              ) : (
                <ChevronExpand size={16} />
              )}
            </button>
          ) : (
            <span className="tdt-expand-placeholder" />
          )}
          {Icon && (
            <Icon
              size={16}
              className={`tdt-row-icon tdt-row-icon--${row.type}`}
            />
          )}
          <span className="tdt-cell-value">
            {column.cell ? column.cell({ row, value }) : value}
          </span>
        </div>
      );
    }

    return column.cell ? column.cell({ row, value }) : value ?? "-";
  };

  return (
    <div className={`tdt-container ${className}`}>
      {/* Toolbar */}
      <div className="tdt-toolbar">
        {/* Search */}
        {enableSearch && (
          <div className="tdt-search">
            <Search className="tdt-search__icon" size={16} />
            <input
              type="text"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search all levels..."
              className="tdt-search__input"
            />
            {globalFilter && (
              <button
                className="tdt-search__clear"
                onClick={() => setGlobalFilter("")}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Toolbar Actions */}
        <div className="tdt-toolbar__actions">
          {/* Expand/Collapse All */}
          <button
            className="tdt-toolbar-btn"
            onClick={() => toggleAllRows(expandedRows.size === 0)}
          >
            {expandedRows.size === 0 ? (
              <>
                <ChevronExpand size={16} />
                <span>Expand All</span>
              </>
            ) : (
              <>
                <ChevronCollapse size={16} />
                <span>Collapse All</span>
              </>
            )}
          </button>

          {/* Columns Toggle */}
          {enableColumnVisibility && (
            <div className="tdt-dropdown" ref={columnsRef}>
              <button
                className="tdt-toolbar-btn"
                onClick={() => setShowColumnsDropdown(!showColumnsDropdown)}
              >
                <Columns3 size={16} />
                <span>Columns</span>
                <span className="tdt-toolbar-btn__badge">
                  {visibleColumnsCount}/{totalColumnsCount}
                </span>
              </button>
              {showColumnsDropdown && (
                <div className="tdt-dropdown__menu tdt-dropdown__menu--columns">
                  <div className="tdt-dropdown__header">
                    <span>Toggle Columns</span>
                    <button
                      className="tdt-dropdown__reset"
                      onClick={() =>
                        setColumnVisibility(
                          columns.reduce(
                            (acc, col) => ({ ...acc, [col.id]: true }),
                            {}
                          )
                        )
                      }
                    >
                      Show All
                    </button>
                  </div>
                  <div className="tdt-dropdown__list">
                    {columns.map((column) => (
                      <label key={column.id} className="tdt-dropdown__item">
                        <input
                          type="checkbox"
                          checked={columnVisibility[column.id]}
                          onChange={() =>
                            setColumnVisibility((prev) => ({
                              ...prev,
                              [column.id]: !prev[column.id],
                            }))
                          }
                        />
                        <span className="tdt-dropdown__checkbox">
                          {columnVisibility[column.id] && <Check size={12} />}
                        </span>
                        <span>{column.header || column.id}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Density */}
          {enableDensity && (
            <div className="tdt-dropdown" ref={densityRef}>
              <button
                className="tdt-toolbar-btn"
                onClick={() => setShowDensityDropdown(!showDensityDropdown)}
              >
                <LayoutGrid size={16} />
                <span>Density</span>
              </button>
              {showDensityDropdown && (
                <div className="tdt-dropdown__menu">
                  <div className="tdt-dropdown__list">
                    {Object.entries(DENSITY_OPTIONS).map(([key, { label }]) => (
                      <button
                        key={key}
                        className={`tdt-dropdown__item tdt-dropdown__item--btn ${
                          density === key ? "is-active" : ""
                        }`}
                        onClick={() => {
                          setDensity(key);
                          setShowDensityDropdown(false);
                        }}
                      >
                        {density === key && <Check size={14} />}
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Export */}
          {enableExport && (
            <button className="tdt-toolbar-btn" onClick={handleExport}>
              <Download size={16} />
              <span>Export</span>
            </button>
          )}
        </div>
      </div>

      {/* Table Wrapper */}
      <div className="tdt-table-wrapper">
        <table className={`tdt-table tdt-table--${density}`}>
          <thead>
            <tr>
              {visibleColumns.map((column, index) => (
                <th
                  key={column.id}
                  className={enableSorting ? "tdt-th--sortable" : ""}
                  style={{ width: column.width }}
                  onClick={() => enableSorting && handleSort(column.id)}
                >
                  <div className="tdt-th__content">
                    {column.header || column.id}
                    {enableSorting && getSortIcon(column.id)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColumnsCount} className="tdt-loading">
                  <div className="tdt-loading__spinner"></div>
                  <span>Loading...</span>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnsCount} className="tdt-empty">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => (
                <tr
                  key={row.id}
                  className={`tdt-row tdt-row--${row.type} tdt-row--level-${
                    row.level
                  } ${onRowClick ? "tdt-row--clickable" : ""}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {visibleColumns.map((column, colIndex) => (
                    <td
                      key={column.id}
                      data-label={column.header}
                      style={{ padding: DENSITY_OPTIONS[density].padding }}
                    >
                      {renderCell(row, column, colIndex === 0)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {enablePagination && (
        <div className="tdt-pagination">
          <div className="tdt-pagination__info">
            <span className="tdt-pagination__showing">
              Showing <strong>{currentPage * rowsPerPage + 1}</strong> to{" "}
              <strong>
                {Math.min((currentPage + 1) * rowsPerPage, sortedData.length)}
              </strong>{" "}
              of <strong>{sortedData.length}</strong> results
            </span>
          </div>

          <div className="tdt-pagination__controls">
            {/* Page Size Selector */}
            <div className="tdt-pagination__size">
              <span>Rows:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            {/* Page Navigation */}
            <div className="tdt-pagination__nav">
              <button
                className="tdt-pagination__btn"
                onClick={() => setCurrentPage(0)}
                disabled={currentPage === 0}
                aria-label="First page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                className="tdt-pagination__btn"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              <span className="tdt-pagination__pages">
                Page <strong>{currentPage + 1}</strong> of{" "}
                <strong>{totalPages || 1}</strong>
              </span>

              <button
                className="tdt-pagination__btn"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={currentPage >= totalPages - 1}
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
              <button
                className="tdt-pagination__btn"
                onClick={() => setCurrentPage(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
                aria-label="Last page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
